import { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime';
import { NotificationManager } from 'react-notifications';
import { useSelector } from 'react-redux';

import { acceptUsageRequest, remindUsageRequestAgain } from '../../../../../services/postTracking.service';
import { downloadFiles, formatNumber, getPlatformImage } from '../../../../../utils/global';
import AvatarComponent from '../../../../common-components/AvatarComponent/AvatarComponent';
import ButtonComponent from '../../../../common-components/ButtonComponent/ButtonComponent';
import CustomTooltip from '../../../../common-components/CustomTooltip/CustomTooltip';
import ModalComponent from '../../../../common-components/ModalComponent/ModalComponent';
import ContentCard from '../ContentCard';
import ViewUsageRightOffer from '../UsageRight/ViewUsageRightOffer';

import '../../styles/request-usage-rights-modal.scss';

import { useCampaignDetail } from '../CampaignDetails/CampaignDetailProvider';

dayjs.extend(RelativeTime);

export default function ViewUsageRightsModal({ onClose, post }) {
  const [isCTALoading, setIsCTALoading] = useState(false);
  const { updatePost, refetchUsageRequests } = useCampaignDetail();
  const { userDetails } = useSelector((state) => state.user);
  const currency = userDetails?.defaultCurrency;

  async function remindAgain() {
    setIsCTALoading(true);
    const res = await remindUsageRequestAgain(post.usageRequestId);
    if (res.status) {
      NotificationManager.success('Reminder Sent');
      onClose();
    } else {
      NotificationManager.error(res?.data?.message ?? 'Failed to send reminder');
    }
    setIsCTALoading(false);
  }

  function renderInfoIcon() {
    return (
      <CustomTooltip title="Revised Duration" placement="top">
        <ErrorOutlineIcon />
      </CustomTooltip>
    );
  }

  async function acceptAndDownload() {
    if (post.usageRightStatus === 'negotiation') {
      setIsCTALoading(true);
      const res = await acceptUsageRequest(post.usageRightId);
      if (!res.status) {
        NotificationManager.error('Failed to accept usage right request');
      }
      refetchUsageRequests();
      updatePost(post._id, {
        usageRightStatus: 'accepted',
      });
    }
    downloadFiles(post.media?.[0]?.url);
    onClose();
    setIsCTALoading(false);
  }

  function renderDays(duration) {
    if (duration === -1) {
      return 'Forever';
    }
    return `${duration} days`;
  }

  function renderCTAButton() {
    switch (post.usageRightStatus) {
      case 'pending':
        return (
          <>
            <ButtonComponent onClick={remindAgain} showLoading={isCTALoading}>
              Remind again
            </ButtonComponent>
            <p>Last message sent {dayjs(post.usageRequest.lastSent).fromNow()}</p>
          </>
        );
      case 'negotiation':
      case 'accepted':
        return (
          <ButtonComponent onClick={acceptAndDownload} showLoading={isCTALoading}>
            Accept & Download
          </ButtonComponent>
        );
      case 'rejected':
        return (
          <ButtonComponent variant="primary-outline" onClick={onClose}>
            Close
          </ButtonComponent>
        );
    }
  }

  return (
    <ModalComponent
      show={true}
      onClose={onClose}
      width={773}
      style={{
        padding: '0',
      }}>
      <div className="view-usage-rights-modal">
        <div className="view-usage-rights-modal__top">
          <div className="view-usage-rights-modal__left">
            <div className="view-usage-rights__profile_details">
              <div className="view-usage-rights__profile_details__top">
                <div className="view-usage-rights__profile_details__top__section">
                  <AvatarComponent alt={post?.profile?.username} src={post?.profile?.imageUrl} size={48} />
                </div>
                {!!post?.profile?.posts && (
                  <div className="view-usage-rights__profile_details__top__section">
                    <div className="view-usage-rights__profile_details__top__section__label">Post</div>
                    <div className="view-usage-rights__profile_details__top__section__value">
                      {post?.profile?.posts}
                    </div>
                  </div>
                )}
                {!!post?.profile?.followers && (
                  <div className="view-usage-rights__profile_details__top__section">
                    <div className="view-usage-rights__profile_details__top__section__label">Followers</div>
                    <div className="view-usage-rights__profile_details__top__section__value">
                      {formatNumber(post?.profile?.followers)}
                    </div>
                  </div>
                )}
                {!!post?.profile.following && (
                  <div className="view-usage-rights__profile_details__top__section">
                    <div className="view-usage-rights__profile_details__top__section__label">Following</div>
                    <div className="view-usage-rights__profile_details__top__section__value">
                      {post?.profile.following}
                    </div>
                  </div>
                )}
              </div>
              <div className="view-usage-rights__profile_details__bottom">
                <div className="view-usage-rights__profile_details__bottom__name">{post?.profile?.username}</div>
                <div className="view-usage-rights__profile_details__bottom__socials">
                  <img src={getPlatformImage(post?.profile?.platform)} alt={`${post?.profile?.platform} logo`} />
                </div>
              </div>
            </div>
            <div className={`view-usage-rights__terms ${post?.usageRightStatus === 'rejected' ? 'rejected' : ''}`}>
              <div className="view-usage-rights__terms__title">Terms for Rights Request</div>
              <div className="view-usage-rights__terms__section">
                <div className="view-usage-rights__terms__section__left">📝 Purpose</div>
                <div className="view-usage-rights__terms__section__right">
                  <span className="view-usage-rights__terms__section__right__original">
                    {post?.usageRequest?.purpose}
                  </span>
                </div>
              </div>
              <div className="view-usage-rights__terms__section">
                <div className="view-usage-rights__terms__section__left">📅 Duration</div>
                <div className="view-usage-rights__terms__section__right">
                  {post?.usageRequestChanges?.duration !== undefined ? (
                    <>
                      <div className="view-usage-rights__terms__section__right__old">
                        {renderDays(post?.usageRequest?.duration)}
                      </div>
                      <div className="view-usage-rights__terms__section__right__new">
                        {renderDays(post?.usageRequestChanges?.duration)}
                      </div>
                      <div className="view-usage-rights__terms__section__right_info">{renderInfoIcon()}</div>
                    </>
                  ) : (
                    <div className="view-usage-rights__terms__section__right__original">
                      {renderDays(post?.usageRequest?.duration)}
                    </div>
                  )}
                </div>
              </div>
              <ViewUsageRightOffer post={post} />
            </div>
            <div className="view-usage-rights__message">
              <div className="view-usage-rights__message__title">
                💌 Message {post.usageRightStatus === 'negotiation' ? 'from' : 'for'} the Creator
              </div>
              <div className="view-usage-rights__message__body">
                {post?.usageRequest?.message && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: post?.usageRequest?.message,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="view-usage-rights-modal__right">
            <ContentCard post={post} hideCardBottom />
          </div>
        </div>
        <div className="view-usage-rights-modal__cta">{renderCTAButton()}</div>
      </div>
    </ModalComponent>
  );
}
